import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import {
  PUBLIC_PLAN_FALLBACKS as publicPlanFallbacks,
  PUBLIC_PLAN_ORDER as publicPlanOrder,
  DEFAULT_TRIAL_DAYS,
} from '../data/billingBrandConfig';

// =====================================================================
// usePublicPlans — NÚCLEO. Planes del sitio público / registro.
//
// PRECIO: la fuente de verdad es subscription_plans, la MISMA tabla que
// leen el cobro (edge functions billing-create-payment,
// mp-create-subscription-payment, astropay-create-payment) y
// useSubscriptionStatus. El sitio público no puede anunciar un precio
// distinto del que después se cobra: app_settings.plans es un override
// legacy que quedó con valores de prueba y hacía publicar $10 en la
// landing mientras la pasarela cobraba $20.000.
//
// RESTO (módulos, límites, días de prueba): overrides de
// app_settings(id='global') si la tabla existe; si no, los fallbacks de
// la capa de marca (billingBrandConfig).
// =====================================================================
const strategicModules = ['produccion-registrar', 'flujo-fondos', 'rentabilidad-ventas'];
const hasAnyModule = (modules, ids) => ids.some((id) => modules.has(id));
const hasAllModules = (modules, ids) => ids.every((id) => modules.has(id));

const describeOperations = (value) => {
  const n = Number(value) || 0;
  if (n <= 0) return 'Operación mensual ilimitada';
  return `Hasta ${n.toLocaleString('es-AR')} operaciones por mes`;
};

const buildHighlights = (modules, fallbackHighlights) => {
  const dyn = [];
  if (hasAnyModule(modules, ['ventas-pos', 'ventas-profesional', 'facturacion'])) dyn.push('Ventas y facturación integradas');
  if (hasAnyModule(modules, ['inventario', 'productos', 'pedido-sugerido'])) dyn.push('Stock y reposición en tiempo real');
  if (hasAnyModule(modules, ['compras-nueva', 'compras', 'compras-ocr', 'proveedores'])) dyn.push('Compras centralizadas');
  if (hasAnyModule(modules, ['bancos', 'tesoreria-cobranzas', 'tesoreria-pagos'])) dyn.push('Caja, cobros y pagos conectados');
  if (hasAnyModule(modules, ['produccion-registrar', 'recetas', 'pedidos-produccion'])) dyn.push('Producción y recetas');
  if (hasAnyModule(modules, ['flujo-fondos'])) dyn.push('Flujo de fondos proyectado');
  if (hasAnyModule(modules, ['rentabilidad-ventas', 'gestion-costos'])) dyn.push('Control de rentabilidad');
  return Array.from(new Set([...fallbackHighlights, ...dyn])).slice(0, 4);
};

const buildPlan = (planKey, adminConfig, billingRow) => {
  const fallback = publicPlanFallbacks[planKey];
  const merged = { ...fallback, ...(adminConfig && typeof adminConfig === 'object' ? adminConfig : {}) };
  const modules = new Set(Array.isArray(merged.modules) && merged.modules.length > 0 ? merged.modules : fallback.modules);
  // Orden de precedencia del precio: lo que se cobra (subscription_plans) →
  // override legacy de app_settings → placeholder de marca (0, que la UI
  // pública muestra como "Consultar"). Un 0 o un valor no numérico en
  // subscription_plans no pisa nada: se cae al siguiente nivel.
  const billingPrice = Number(billingRow?.price_monthly);
  const settingsPrice = Number(merged.price_monthly);
  const price = billingPrice > 0
    ? billingPrice
    : (settingsPrice > 0 ? settingsPrice : fallback.price_monthly);
  const operationLimit = Number.isFinite(Number(merged.operation_limit)) ? Number(merged.operation_limit) : fallback.operation_limit;
  const fallbackMaxUsers = planKey === 'basic' ? 3 : planKey === 'premium' ? 6 : 10;
  const maxUsers = Number(merged.max_users) > 0 ? Number(merged.max_users) : fallbackMaxUsers;
  const monthlyLimits = merged.monthly_limits && typeof merged.monthly_limits === 'object' ? merged.monthly_limits : {};

  return {
    key: planKey,
    adminLabel: merged.label || fallback.marketingName,
    marketingName: fallback.marketingName,
    badge: fallback.badge,
    audience: fallback.audience,
    summary: fallback.summary,
    cta: fallback.cta,
    price_monthly: price,
    operation_limit: operationLimit,
    operationLabel: describeOperations(operationLimit),
    modules: Array.from(modules),
    highlights: buildHighlights(modules, fallback.highlights),
    includesStrategySuite: hasAllModules(modules, strategicModules),
    max_users: maxUsers,
    monthly_limits: monthlyLimits,
    usersLabel: `Hasta ${maxUsers} usuario${maxUsers === 1 ? '' : 's'}`,
  };
};

export function usePublicPlans() {
  const [plansByKey, setPlansByKey] = useState(() =>
    publicPlanOrder.reduce((acc, key) => { acc[key] = buildPlan(key, publicPlanFallbacks[key]); return acc; }, {})
  );
  const [trialDays, setTrialDays] = useState(DEFAULT_TRIAL_DAYS);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('fallback');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // Las dos consultas son independientes a propósito: cualquiera de las
        // dos tablas puede no existir según la app (app_settings en Impulza,
        // subscription_plans donde el cobro todavía no se activó) y la falta de
        // una no puede tumbar a la otra. Por eso allSettled y no Promise.all.
        const [settingsResult, billingResult] = await Promise.allSettled([
          supabase.from('app_settings').select('default_trial_days, plans').eq('id', 'global').maybeSingle(),
          // Se piden TODOS los planes, activos o no: un plan despublicado
          // igual tiene que mostrar su precio real y nunca uno de prueba.
          supabase.from('subscription_plans').select('key, price_monthly'),
        ]);
        if (!alive) return;

        const settingsData = settingsResult.status === 'fulfilled' && !settingsResult.value?.error
          ? settingsResult.value.data
          : null;
        const billingRows = billingResult.status === 'fulfilled' && !billingResult.value?.error
          ? (billingResult.value.data || [])
          : [];
        const billingByKey = billingRows.reduce((acc, row) => {
          if (row?.key) acc[row.key] = row;
          return acc;
        }, {});

        setPlansByKey(publicPlanOrder.reduce((acc, key) => {
          acc[key] = buildPlan(key, settingsData?.plans?.[key], billingByKey[key]);
          return acc;
        }, {}));
        setTrialDays(Number(settingsData?.default_trial_days) || DEFAULT_TRIAL_DAYS);
        setSource(
          Object.keys(billingByKey).length > 0
            ? 'subscription-plans'
            : (settingsData?.plans ? 'super-admin' : 'fallback')
        );
      } catch (_) {
        // Ninguna tabla disponible: fallback silencioso a la capa de marca.
        if (!alive) return;
        setPlansByKey(publicPlanOrder.reduce((acc, key) => { acc[key] = buildPlan(key, publicPlanFallbacks[key]); return acc; }, {}));
        setSource('fallback');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const plans = useMemo(() => publicPlanOrder.map((key) => plansByKey[key]), [plansByKey]);
  return { plans, trialDays, loading, source };
}

export default usePublicPlans;
